import { useMutation } from '@apollo/client';
import { PROFESSIONAL_UPDATE_CLIENT_ALLERGIES, PROFESSIONAL_UPDATE_CLIENT_BIO, PROFESSIONAL_UPDATE_CLIENT_BLOODLINE_DISEASES, PROFESSIONAL_UPDATE_CLIENT_CONTACT_DETAILS, PROFESSIONAL_UPDATE_CLIENT_OTHER_MEDICATIONS, PROFESSIONAL_UPDATE_CLIENT_SPECIALISTS, PROFESSIONAL_UPDATE_CLIENT_VACCINATIONS, PROFESSIONAL_UPDATE_CLIENT_SURGERIES, PROFESSIONAL_UPDATE_CLIENT_IMPORTANT_NOTES, PROFESSIONAL_UPDATE_CLIENT_FEMALE_BIO } from '../../services/graphql/mutations/professional';
import { useCallback } from 'react';
import { CareerAndEducationDetailType, FamilyDetailType, PersonalContactDetailsType, SpecialistsType, VaccinationType } from '../../types';

const buildFemaleBioParams = (values: any) => {
  const femaleBio = values?.clientBioFemale || values || {};
  const params: Record<string, any> = {};

  if (femaleBio.firstMensAge !== undefined && femaleBio.firstMensAge !== null && femaleBio.firstMensAge !== '') {
    const parsedAge = Number(femaleBio.firstMensAge);
    if (!Number.isNaN(parsedAge)) {
      params.firstMensAge = parsedAge;
    }
  }

  if (femaleBio.isInMenopause !== undefined && femaleBio.isInMenopause !== null) {
    if (typeof femaleBio.isInMenopause === 'string') {
      params.isInMenopause = femaleBio.isInMenopause.toLowerCase() === 'true';
    } else {
      params.isInMenopause = Boolean(femaleBio.isInMenopause);
    }
  }

  return params;
};

type UpdateProfessionalClientSpecialists = {
  otherSpecialists: SpecialistsType[] & { _destroy?: boolean }
  _destroy?: boolean
} & SpecialistsType;

type UpdateProfessionalClientContactDetailsType = {
  personalContactDetailAttributes: PersonalContactDetailsType
  careerAndEducationDetailAttributes: CareerAndEducationDetailType
  familyDetailAttributes: FamilyDetailType
}

type UpdateProfessionalClientVaccinationsType = {
  optionVaccinationId: string
} & VaccinationType;

export const useUpdateProfessionalClientBio = () => {
  const [updateProfessionalClientBio, { loading }] = useMutation(PROFESSIONAL_UPDATE_CLIENT_BIO);

  const updateData = useCallback(async (clientId: string, values: any) => {
    try {
      const { data } = await updateProfessionalClientBio({
        variables: {
          input: {
            clientId,
            params: {
              ...(values.firstName && { firstName: values.firstName }),
              ...(values.familyName && { familyName: values.familyName }),
              bioAttributes: {
                ...(values.sex && { sex: values.sex }),
                ...(values.weight && { weight: values.weight }),
                ...(values.waistCircumference && { waistCircumference: values.waistCircumference }),
                ...(values.isVegetarian !== undefined && { isVegetarian: values.isVegetarian }),
                ...(values.dateOfBirth && { dateOfBirth: values.dateOfBirth }),
                ...(values.length && { length: values.length }),
              },
            },
          },
        }
      });
      return data;
    } catch (error) {
      console.log('Error in updateProfessionalClientBio: ', error);
    }
  }, [updateProfessionalClientBio]);

  return {
    update: updateData,
    loading
  };
};

export const useUpdateProfessionalClientFemaleBio = () => {
  const [updateProfessionalClientFemaleBio, { loading }] = useMutation(PROFESSIONAL_UPDATE_CLIENT_FEMALE_BIO);

  const updateData = useCallback(async (clientId: string, values: any) => {
    try {
      const params = buildFemaleBioParams(values);
      const { data } = await updateProfessionalClientFemaleBio({
        variables: {
          input: {
            clientId,
            params
          }
        }
      });
      return data;
    } catch (error) {
      console.log('Error in updateProfessionalClientFemaleBio: ', error);
      throw error;
    }
  }, [updateProfessionalClientFemaleBio]);

  return {
    update: updateData,
    loading
  };
};

export const useUpdateProfessionalClientSpecialists = () => {
  const [updateProfessionalClientSpecialists, { loading }] = useMutation(PROFESSIONAL_UPDATE_CLIENT_SPECIALISTS);

  const updateData = useCallback(async (clientId: string, values: UpdateProfessionalClientSpecialists) => {
    try {
      const { data } = await updateProfessionalClientSpecialists({
        variables: {
          input: {
            clientId,
            specialistsAttributes: {
              id: values?.id,
              name: values?.name,
              address: values?.address,
              telephoneCountryCode: values?.telephoneCountryCode,
              telephoneNumber: values?.telephoneNumber,
              forWhat: values?.forWhat,
              sinceWhen: values?.sinceWhen,
              _destroy: values?._destroy
            },
            otherHealthProfessionalsAttributes: values?.otherSpecialists
          },
        }
      });
      return data;
    } catch (error) {
      console.log('Error in updateProfessionalClientSpecialists: ', error);
    }
  }, [updateProfessionalClientSpecialists]);

  return {
    update: updateData,
    loading
  };
};

export const useUpdateProfessionalClientContactDetails = () => {
  const [updateProfessionalClientContactDetails, { loading }] = useMutation(PROFESSIONAL_UPDATE_CLIENT_CONTACT_DETAILS);

  const updateData = useCallback(async (clientId: string, values: UpdateProfessionalClientContactDetailsType) => {
    try {
      const { data } = await updateProfessionalClientContactDetails({
        variables: {
          input: {
            clientId,
            params: {
              familyDetailAttributes: values.familyDetailAttributes,
              careerAndEducationDetailAttributes: values.careerAndEducationDetailAttributes,
              personalContactDetailAttributes: {
                ...values.personalContactDetailAttributes,
                telephoneCountryCode: values.personalContactDetailAttributes?.telephoneCountryCode || ' '
              }
            }
          },
        }
      });
      return data;
    } catch (error) {
      console.log('Error in updateProfessionalClientContactDetails: ', error);
    }
  }, [updateProfessionalClientContactDetails]);

  return {
    update: updateData,
    loading
  };
};

export const useUpdateProfessionalClientVaccinations = () => {
  const [updateProfessionalClientVaccinations, { loading }] = useMutation(PROFESSIONAL_UPDATE_CLIENT_VACCINATIONS);

  const updateData = useCallback(async (clientId: string, values: any[]) => {
    try {
      const { data } = await updateProfessionalClientVaccinations({
        variables: {
          input: {
            clientId,
            params: values,
          },
        }
      });
      return data;
    } catch (error) {
      console.log('Error in updateProfessionalClientVaccinations: ', error);
    }
  }, [updateProfessionalClientVaccinations]);

  return {
    update: updateData,
    loading
  };
};

export const useUpdateProfessionalClientAllergies = () => {
  const [updateProfessionalClientAllergies, { loading }] = useMutation(PROFESSIONAL_UPDATE_CLIENT_ALLERGIES);

  const updateData = useCallback(async (clientId: string, values: any) => {
    try {
      const { data } = await updateProfessionalClientAllergies({
        variables: {
          input: {
            clientId,
            params: {
              ...values
            }
          },
        }
      });
      return data;
    } catch (error) {
      console.log('Error in updateProfessionalClientAllergies: ', error);
    }
  }, [updateProfessionalClientAllergies]);

  return {
    update: updateData,
    loading
  };
};

export const useUpdateProfessionalClientOtherMedications = () => {
  const [updateProfessionalClientOtherMedications, { loading }] = useMutation(PROFESSIONAL_UPDATE_CLIENT_OTHER_MEDICATIONS);

  const updateData = useCallback(async (clientId: string, values: any) => {
    try {
      const { data } = await updateProfessionalClientOtherMedications({
        variables: {
          input: {
            clientId,
            params: {
              ...values
            }
          },
        }
      });
      return data;
    } catch (error) {
      console.log('Error in updateProfessionalClientOtherMedications: ', error);
    }
  }, [updateProfessionalClientOtherMedications]);

  return {
    update: updateData,
    loading
  };
};

export const useUpdateProfessionalClientBloodlineDiseases = () => {
  const [updateProfessionalClientBloodlineDiseases, { loading }] = useMutation(PROFESSIONAL_UPDATE_CLIENT_BLOODLINE_DISEASES);

  const updateData = useCallback(async (clientId: string, values: any[]) => {
    try {
      const { data } = await updateProfessionalClientBloodlineDiseases({
        variables: {
          input: {
            clientId,
            params: values,
          },
        }
      });
      return data;
    } catch (error) {
      console.log('Error in updateProfessionalClientBloodlineDiseases: ', error);
    }
  }, [updateProfessionalClientBloodlineDiseases]);

  return {
    update: updateData,
    loading
  };
};

export const useUpdateProfessionalClientSurgeries = () => {
  const [updateProfessionalClientSurgeries, { loading }] = useMutation(PROFESSIONAL_UPDATE_CLIENT_SURGERIES);

  const updateData = useCallback(async (clientId: string, values: any[]) => {
    try {
      const { data } = await updateProfessionalClientSurgeries({
        variables: {
          input: {
            clientId,
            params: values,
          },
        }
      });
      return data;
    } catch (error) {
      console.log('Error in updateProfessionalClientSurgeries: ', error);
    }
  }, [updateProfessionalClientSurgeries]);

  return {
    update: updateData,
    loading
  };
};

export const useUpdateProfessionalClientImportantNotes = () => {
  const [updateProfessionalClientImportantNotes, { loading }] = useMutation(PROFESSIONAL_UPDATE_CLIENT_IMPORTANT_NOTES);

  const updateData = useCallback(async (clientId: string, values: any[]) => {
    try {
      const { data } = await updateProfessionalClientImportantNotes({
        variables: {
          input: {
            clientId,
            params: values,
          },
        }
      });
      return data;
    } catch (error) {
      console.log('Error in updateProfessionalClientImportantNotes: ', error);
      throw error;
    }
  }, [updateProfessionalClientImportantNotes]);

  return {
    update: updateData,
    loading
  };
};
